import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui';
import { Sello } from '@/components/hallazgos/Sello';
import { ChevronDown, ChevronRight, Download, ExternalLink, FileUp, ListChecks, Search, Trash2 } from 'lucide-react';
import { Fragment, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Seguimiento de actas con problemas pendientes, a partir del JSON que deja el diagnóstico
 * (pendientes_actas.json). No toca la base de datos: el seguimiento (estado y notas) se guarda en
 * este navegador y se exporta como un JSON actualizado para compartirlo o volver a importarlo.
 */

type EstadoPendiente = 'PENDIENTE' | 'RESUELTA' | 'REVISADA_SIN_CAMBIOS';

interface ItemPendiente {
  asesoriaId: number;
  orden: number | null;
  empresaNombre: string | null;
  empresaNit: string | null;
  asesorNombre: string | null;
  docTitulo: string | null;
  actaUrl: string | null;
  docId?: string | null;
  tipo: string;
  estado: EstadoPendiente;
  detalle?: string | null;
  accion?: string | null;
  razon: string;
  detectadoEn?: string;
  nota?: string;
  actualizadoEn?: string;
  /** Códigos de PROBLEMAS: un acta puede tener varios a la vez. */
  problemas?: string[];
  diagnostico?: Diagnostico;
}

type Calificacion = 'C' | 'NC' | 'CP' | 'NA';

/** Cruce del acta con la evaluación de su asesoría y con el contenido del documento. */
interface Diagnostico {
  categoriaEvaluacion?: string;
  calificacionesPropias?: Partial<Record<Calificacion, number>>;
  /** p. ej. { "NC->C": 8, "CP->C": 2 } */
  cambiosCalificacion?: Record<string, number>;
  ternasConCambios?: number;
  tieneFilaAudios?: boolean;
  totalAudios?: number;
  duracionTotal?: string | null;
  accionesEnActa?: string | null;
  compromisosEnActa?: string | null;
  filaEmpresaEnActa?: string | null;
  nombreAnterior?: string | null;
}

interface ArchivoPendientes {
  generadoEn?: string;
  version?: number;
  estados?: Record<string, string>;
  items: ItemPendiente[];
}

const STORAGE_KEY = 'sysadmin.pendientesActas';
const ESTADOS: { valor: EstadoPendiente; label: string; variant: 'yellow' | 'green' | 'gray' }[] = [
  { valor: 'PENDIENTE', label: 'Pendiente', variant: 'yellow' },
  { valor: 'RESUELTA', label: 'Resuelta', variant: 'green' },
  { valor: 'REVISADA_SIN_CAMBIOS', label: 'Revisada sin cambios', variant: 'gray' },
];
const TIPOS: Record<string, { label: string; ruta?: string; variant: 'red' | 'purple' | 'blue' | 'gray' }> = {
  AUDIOS_AJENOS: { label: 'Audios de otro asesor', ruta: '/admin/correcciones-asesorias', variant: 'red' },
  FORMATO_ANTIGUO: { label: 'Formato antiguo', ruta: '/admin/regenerar-actas', variant: 'purple' },
  FILAS_SOBRANTES: { label: 'Filas sobrantes', variant: 'blue' },
  FALSO_POSITIVO: { label: 'Falso positivo', variant: 'gray' },
};
const PROBLEMAS: Record<string, { label: string; variant: 'red' | 'yellow' | 'purple' | 'blue' | 'gray' | 'warning' }> = {
  SIN_FILA_AUDIOS: { label: 'Sin fila AUDIOS', variant: 'blue' },
  NOMBRE_FORMATO_ANTERIOR: { label: 'Nombre de archivo anterior', variant: 'gray' },
  ACCIONES_FALTANTES: { label: 'Acciones faltantes', variant: 'red' },
  TERNAS_SIN_CALIFICACION: { label: 'Ternas sin calificación', variant: 'warning' },
  SIN_EVALUACION: { label: 'Sin evaluación', variant: 'gray' },
  TEXTO_DE_PRUEBA: { label: 'Texto de prueba en encabezado', variant: 'yellow' },
  COMPROMISOS_JSON: { label: 'Compromisos con formato interno', variant: 'purple' },
};
const CATEGORIAS_EVALUACION: Record<string, string> = {
  SIN_EVALUACION: 'Sin calificaciones ni cambios en ternas',
  SOLO_TERNAS: 'Solo cambios en ternas',
  SOLO_CALIFICACIONES: 'Solo calificaciones',
  COMPLETA: 'Calificaciones y ternas',
};
const PAGE_SIZE = 50;

function leerGuardado(): ArchivoPendientes | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ArchivoPendientes) : null;
  } catch {
    return null;
  }
}

function guardar(archivo: ArchivoPendientes | null) {
  try {
    if (archivo) localStorage.setItem(STORAGE_KEY, JSON.stringify(archivo));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Sin almacenamiento (modo privado, cuota): la vista sigue funcionando en memoria.
  }
}

function validarArchivo(data: unknown): ArchivoPendientes {
  const items = (data as ArchivoPendientes)?.items;
  if (!Array.isArray(items)) throw new Error('El archivo no tiene la lista "items"');
  const validos = items.filter(
    (i) => i && Number.isInteger(i.asesoriaId) && typeof i.tipo === 'string' && ESTADOS.some((e) => e.valor === i.estado)
  );
  if (validos.length === 0) throw new Error('Ningún item tiene asesoriaId, tipo y estado válidos');
  return { ...(data as ArchivoPendientes), items: validos.map((i) => ({ ...i, razon: i.razon ?? i.detalle ?? '' })) };
}

const claveItem = (i: ItemPendiente) => `${i.asesoriaId}:${i.tipo}`;

export function PendientesActasPage() {
  const [archivo, setArchivo] = useState<ArchivoPendientes | null>(leerGuardado);
  const [error, setError] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<EstadoPendiente | 'TODOS'>('PENDIENTE');
  const [filtroTipo, setFiltroTipo] = useState<string>('TODOS');
  const [filtroProblema, setFiltroProblema] = useState<string>('TODOS');
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);
  // Corregir es solo de sysadmin; la revisión de hallazgos (correo con "+") revisa y lleva el seguimiento.
  const esSysadmin = useAuth().user?.asesor?.rol === 'SYSADMIN';

  const actualizar = (nuevo: ArchivoPendientes | null) => {
    setArchivo(nuevo);
    guardar(nuevo);
  };

  const importar = async (file: File) => {
    setError(null);
    try {
      const nuevo = validarArchivo(JSON.parse(await file.text()));
      // Si ya había seguimiento, se conservan estado y notas de las actas que coinciden.
      const previos = new Map((archivo?.items ?? []).map((i) => [claveItem(i), i]));
      nuevo.items = nuevo.items.map((i) => {
        const p = previos.get(claveItem(i));
        return p?.actualizadoEn ? { ...i, estado: p.estado, nota: p.nota, actualizadoEn: p.actualizadoEn } : i;
      });
      actualizar(nuevo);
      setPagina(1);
    } catch (e: any) {
      setError(`No se pudo importar: ${e.message}`);
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const cambiarItem = (clave: string, cambios: Partial<ItemPendiente>) => {
    if (!archivo) return;
    actualizar({
      ...archivo,
      items: archivo.items.map((i) => (claveItem(i) === clave ? { ...i, ...cambios, actualizadoEn: new Date().toISOString() } : i)),
    });
  };

  const descargar = (contenido: unknown, nombre: string) => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(contenido, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportar = () => {
    if (!archivo) return;
    const fecha = new Date().toISOString();
    descargar(
      { ...archivo, exportadoEn: fecha, asesoriaIds: archivo.items.filter((i) => i.estado === 'PENDIENTE').map((i) => i.asesoriaId) },
      `pendientes_actas_${fecha.slice(0, 10)}.json`
    );
  };

  const items = archivo?.items ?? [];
  // Tipos y problemas se cuentan dentro del estado elegido: un tipo ya resuelto (p. ej. Formato
  // antiguo) no aparece entre los pendientes.
  const itemsDelEstado = useMemo(
    () => (filtroEstado === 'TODOS' ? items : items.filter((i) => i.estado === filtroEstado)),
    [items, filtroEstado]
  );
  // El tipo elegido se mantiene visible aunque no tenga items en este estado, para poder quitarlo.
  const tiposPresentes = useMemo(
    () => [...new Set([...itemsDelEstado.map((i) => i.tipo), ...(filtroTipo !== 'TODOS' ? [filtroTipo] : [])])],
    [itemsDelEstado, filtroTipo]
  );
  const problemasPresentes = useMemo(() => {
    const c: Record<string, number> = {};
    for (const i of itemsDelEstado) for (const p of i.problemas ?? []) c[p] = (c[p] ?? 0) + 1;
    return c;
  }, [itemsDelEstado]);
  const alternar = (clave: string) =>
    setAbiertos((prev) => {
      const s = new Set(prev);
      if (s.has(clave)) s.delete(clave);
      else s.add(clave);
      return s;
    });
  const conteo = useMemo(() => {
    const c: Record<string, number> = {};
    for (const i of items) c[i.estado] = (c[i.estado] ?? 0) + 1;
    return c;
  }, [items]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return items.filter((i) => {
      if (filtroEstado !== 'TODOS' && i.estado !== filtroEstado) return false;
      if (filtroTipo !== 'TODOS' && i.tipo !== filtroTipo) return false;
      if (filtroProblema !== 'TODOS' && !(i.problemas ?? []).includes(filtroProblema)) return false;
      if (!q) return true;
      return [i.asesoriaId, i.empresaNombre, i.empresaNit, i.asesorNombre, i.docTitulo, i.razon, i.nota]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, filtroEstado, filtroTipo, filtroProblema, busqueda]);

  // Exporta la lista filtrada en el formato que acepta "Importar actas (JSON)" de la migración de horas.
  const exportarParaMigracion = () =>
    descargar(
      {
        filtro: `${filtroTipo}/${filtroProblema}/${filtroEstado}`,
        asesoriaIds: [...new Set(filtrados.map((i) => i.asesoriaId))],
        detalles: filtrados,
      },
      `asesorias_${(filtroProblema !== 'TODOS' ? filtroProblema : filtroTipo).toLowerCase()}_${new Date().toISOString().slice(0, 10)}.json`
    );

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE));
  const visibles = filtrados.slice((pagina - 1) * PAGE_SIZE, pagina * PAGE_SIZE);

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.03em] text-(--admin-text)">Actas con hallazgos</h1>
          <p className="mt-1 max-w-2xl text-[13px] text-(--admin-text2)">
            Carga el diagnóstico, revisa la razón de cada acta y marca su estado. El seguimiento queda en este navegador
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && void importar(e.target.files[0])}
          />
          <Button variant="secondary" onClick={() => inputRef.current?.click()}>
            <FileUp className="w-4 h-4 mr-2" />
            Cargar diagnóstico
          </Button>
          <Button variant="secondary" onClick={exportar} disabled={!archivo}>
            <Download className="w-4 h-4 mr-2" />
            Descargar seguimiento
          </Button>
          {archivo && (
            <Button
              variant="ghost"
              onClick={() => window.confirm('¿Quitar el diagnóstico cargado y su seguimiento de este navegador?') && actualizar(null)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Quitar
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-3 py-2 text-sm text-(--admin-red)">{error}</div>
      )}

      {!archivo ? (
        <div className="rounded-(--admin-r2) border border-dashed border-(--admin-border) bg-(--admin-surface) p-10 text-center text-sm text-(--admin-text2)">
          <ListChecks className="w-8 h-8 mx-auto mb-2 text-(--admin-text3)" />
          Carga el archivo del diagnóstico (<span className="font-mono">pendientes_actas.json</span>) para empezar
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[{ valor: 'TODOS' as const, label: 'Todas', n: items.length }, ...ESTADOS.map((e) => ({ ...e, n: conteo[e.valor] ?? 0 }))].map(
              (e) => (
                <button
                  key={e.valor}
                  onClick={() => {
                    setFiltroEstado(e.valor);
                    setPagina(1);
                  }}
                  className={`p-3 rounded-(--admin-r2) border text-left transition-colors ${
                    filtroEstado === e.valor
                      ? 'border-(--admin-text) bg-(--admin-surface2)'
                      : 'border-(--admin-border) bg-(--admin-surface) hover:bg-(--admin-surface2)'
                  }`}
                >
                  <span className="block text-xs text-(--admin-text2)">{e.label}</span>
                  <span className="text-2xl font-bold text-(--admin-text) tabular-nums">{e.n}</span>
                </button>
              )
            )}
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="flex flex-wrap gap-2">
              {['TODOS', ...tiposPresentes].map((t) => (
                <button
                  key={t}
                  onClick={() => {
                    setFiltroTipo(t);
                    setPagina(1);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    filtroTipo === t
                      ? 'bg-(--admin-text) text-white'
                      : 'bg-(--admin-surface2) text-(--admin-text2) hover:bg-(--admin-border)'
                  }`}
                >
                  {t === 'TODOS' ? 'Todos los tipos' : (TIPOS[t]?.label ?? t)} (
                  {t === 'TODOS' ? itemsDelEstado.length : itemsDelEstado.filter((i) => i.tipo === t).length})
                </button>
              ))}
            </div>
            <div className="relative md:ml-auto md:w-72">
              <Search className="w-4 h-4 text-(--admin-text3) absolute left-3 top-2.5" />
              <input
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPagina(1);
                }}
                placeholder="Empresa, NIT, asesor, ID, razón…"
                className="w-full bg-(--admin-surface) border border-(--admin-border) rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-(--admin-border2)"
              />
            </div>
            <Button
              variant="secondary"
              onClick={exportarParaMigracion}
              disabled={filtrados.length === 0}
              title="Archivo para cargar en la migración de horas"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar para migración ({filtrados.length})
            </Button>
          </div>

          {Object.keys(problemasPresentes).length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-(--admin-text2)">Problema:</span>
              {['TODOS', ...Object.keys(PROBLEMAS).filter((p) => problemasPresentes[p])].map((p) => (
                <button
                  key={p}
                  onClick={() => {
                    setFiltroProblema(p);
                    setPagina(1);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    filtroProblema === p
                      ? 'bg-(--admin-text) text-white'
                      : 'bg-(--admin-surface2) text-(--admin-text2) hover:bg-(--admin-border)'
                  }`}
                >
                  {p === 'TODOS' ? 'Todos' : PROBLEMAS[p].label} ({p === 'TODOS' ? itemsDelEstado.length : problemasPresentes[p]})
                </button>
              ))}
            </div>
          )}

          <div className="bg-(--admin-surface) rounded-(--admin-r2) border border-(--admin-border) overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-(--admin-text2)">
                <thead className="bg-(--admin-surface2) text-(--admin-text3) uppercase tracking-wider">
                  <tr>
                    <th className="py-2.5 px-3">Asesoría</th>
                    <th className="py-2.5 px-3">Empresa / Asesor</th>
                    <th className="py-2.5 px-3">Tipo</th>
                    <th className="py-2.5 px-3">Razón</th>
                    <th className="py-2.5 px-3">Seguimiento</th>
                    <th className="py-2.5 px-3 text-right">Ir a</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--admin-border)">
                  {visibles.map((i) => {
                    const clave = claveItem(i);
                    const tipo = TIPOS[i.tipo];
                    const abierto = abiertos.has(clave);
                    return (
                      <Fragment key={clave}>
                        <tr className="align-top">
                          <td className="py-2.5 px-3 font-mono font-semibold text-(--admin-text) whitespace-nowrap">
                            #{i.asesoriaId}
                            {i.orden !== null && <span className="text-(--admin-text3) font-normal ml-1">({i.orden})</span>}
                          </td>
                          <td className="py-2.5 px-3 min-w-48">
                            <div className="font-semibold text-(--admin-text)">{i.empresaNombre ?? '—'}</div>
                            <div className="text-[11px] text-(--admin-text2)">
                              NIT {i.empresaNit ?? '—'} · {i.asesorNombre ?? '—'}
                            </div>
                            {i.docTitulo && (
                              <div className="text-[11px] font-mono text-(--admin-text3) truncate max-w-56">{i.docTitulo}</div>
                            )}
                          </td>
                          <td className="py-2.5 px-3">
                            <Sello>{tipo?.label ?? i.tipo}</Sello>
                            {(i.problemas ?? []).length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1 max-w-56">
                                {i.problemas!.map((p) => (
                                  <Sello key={p}>{PROBLEMAS[p]?.label ?? p}</Sello>
                                ))}
                              </div>
                            )}
                            {i.diagnostico && (
                              <button
                                onClick={() => alternar(clave)}
                                className="mt-1.5 flex items-center gap-1 text-[11px] text-(--admin-blue) hover:underline"
                              >
                                {abierto ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                Diagnóstico
                              </button>
                            )}
                          </td>
                          <td className="py-2.5 px-3 max-w-md text-(--admin-text2)">
                            {i.razon}
                            {i.accion && i.estado === 'PENDIENTE' && <div className="mt-1 text-[11px] text-(--admin-blue)">{i.accion}</div>}
                          </td>
                          <td className="py-2.5 px-3 min-w-52 space-y-1.5">
                            <select
                              value={i.estado}
                              onChange={(e) => cambiarItem(clave, { estado: e.target.value as EstadoPendiente })}
                              className="w-full border border-(--admin-border) rounded-md px-2 py-1 text-xs"
                              aria-label="Estado"
                            >
                              {ESTADOS.map((e) => (
                                <option key={e.valor} value={e.valor}>
                                  {e.label}
                                </option>
                              ))}
                            </select>
                            <textarea
                              defaultValue={i.nota ?? ''}
                              onBlur={(e) => e.target.value !== (i.nota ?? '') && cambiarItem(clave, { nota: e.target.value })}
                              placeholder="Nota…"
                              rows={2}
                              className="w-full border border-(--admin-border) rounded-md px-2 py-1 text-xs resize-y"
                            />
                            {i.actualizadoEn && (
                              <div className="text-[11px] text-(--admin-text3)">
                                Actualizado {new Date(i.actualizadoEn).toLocaleString('es-CO')}
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right space-y-1 whitespace-nowrap">
                            {i.actaUrl && (
                              <a
                                href={i.actaUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-end gap-1 text-(--admin-blue) hover:underline"
                              >
                                Acta <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                            {esSysadmin && tipo?.ruta && i.estado === 'PENDIENTE' && (
                              <Link to={tipo.ruta} className="block text-(--admin-blue) hover:underline">
                                Corregir →
                              </Link>
                            )}
                          </td>
                        </tr>
                        {abierto && i.diagnostico && (
                          <tr className="bg-(--admin-surface2)">
                            <td colSpan={6} className="px-3 py-3">
                              <DetalleDiagnostico d={i.diagnostico} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {visibles.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-(--admin-text3)">
                        No hay actas con estos filtros
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPaginas > 1 && (
              <div className="flex items-center justify-end gap-2 p-3 border-t border-(--admin-border) text-xs">
                <Button variant="ghost" size="sm" onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1}>
                  Anterior
                </Button>
                <span className="tabular-nums">
                  {pagina} / {totalPaginas}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setPagina(pagina + 1)} disabled={pagina >= totalPaginas}>
                  Siguiente
                </Button>
              </div>
            )}
          </div>
          {archivo.generadoEn && (
            <p className="text-[11px] text-(--admin-text3)">
              Diagnóstico generado el {new Date(archivo.generadoEn).toLocaleString('es-CO')}
            </p>
          )}
        </>
      )}
    </div>
  );
}

const CALIFICACIONES: { valor: Calificacion; label: string; clase: string }[] = [
  { valor: 'C', label: 'Cumple', clase: 'bg-(--admin-green-bg) text-(--admin-green)' },
  { valor: 'CP', label: 'Cumple parcialmente', clase: 'bg-(--admin-amber-bg) text-(--admin-amber)' },
  { valor: 'NC', label: 'No cumple', clase: 'bg-(--admin-red-bg) text-(--admin-red)' },
  { valor: 'NA', label: 'No aplica', clase: 'bg-(--admin-surface2) text-(--admin-text2)' },
];

function DetalleDiagnostico({ d }: { d: Diagnostico }) {
  const cambios = Object.entries(d.cambiosCalificacion ?? {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="grid md:grid-cols-3 gap-4 text-xs">
      <section className="space-y-2">
        <h4 className="font-semibold text-(--admin-text)">Evaluación de la asesoría</h4>
        {d.categoriaEvaluacion && (
          <div className="text-(--admin-text2)">{CATEGORIAS_EVALUACION[d.categoriaEvaluacion] ?? d.categoriaEvaluacion}</div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {CALIFICACIONES.map((c) => (
            <span key={c.valor} title={c.label} className={`px-2 py-0.5 rounded-md font-mono font-semibold ${c.clase}`}>
              {c.valor} {d.calificacionesPropias?.[c.valor] ?? 0}
            </span>
          ))}
        </div>
        <div className="text-(--admin-text2)">
          Ternas con cambios (observaciones, plan de mejora o fecha): <b className="text-(--admin-text)">{d.ternasConCambios ?? 0}</b>
        </div>
        {cambios.length > 0 && (
          <div>
            <div className="text-(--admin-text2) mb-1">Cambios de calificación</div>
            <div className="flex flex-wrap gap-1.5">
              {cambios.map(([k, n]) => (
                <span key={k} className="px-2 py-0.5 rounded-md bg-(--admin-surface) border border-(--admin-border) font-mono">
                  {k.replace('->', ' → ')} <b>{n}</b>
                </span>
              ))}
            </div>
          </div>
        )}
      </section>
      <section className="space-y-2">
        <h4 className="font-semibold text-(--admin-text)">Documento</h4>
        {d.nombreAnterior && (
          <div className="text-(--admin-text2)">
            Nombre anterior: <span className="font-mono text-(--admin-text)">{d.nombreAnterior}</span>
          </div>
        )}
        <div className="text-(--admin-text2)">
          Fila AUDIOS: <b className="text-(--admin-text)">{d.tieneFilaAudios ? 'Sí' : 'No'}</b>
          {d.totalAudios != null && (
            <span className="text-(--admin-text2)">
              {' '}
              · {d.totalAudios} audio(s){d.duracionTotal ? ` · ${d.duracionTotal}` : ''}
            </span>
          )}
        </div>
        {d.filaEmpresaEnActa != null && (
          <div className="text-(--admin-text2)">
            Encabezado empresa: <span className="font-mono text-(--admin-text)">{d.filaEmpresaEnActa || '—'}</span>
          </div>
        )}
        {d.compromisosEnActa != null && (
          <div>
            <div className="text-(--admin-text2)">Compromisos en el acta</div>
            <p className="text-(--admin-text) line-clamp-4 whitespace-pre-wrap">{d.compromisosEnActa || '—'}</p>
          </div>
        )}
      </section>
      <section className="space-y-1">
        <h4 className="font-semibold text-(--admin-text)">Acciones adelantadas en el acta</h4>
        <p className="text-(--admin-text) whitespace-pre-wrap max-h-40 overflow-y-auto">{d.accionesEnActa || '—'}</p>
      </section>
    </div>
  );
}
